import ResponsiveImage from 'components/Common/ResponsiveImage'
import BodyText from 'LuxKit/Typography/BodyText'
import Heading from 'LuxKit/Typography/Heading'
import { mediaQueryUp } from 'components/utils/breakpoint'
import { rem } from 'polished'
import styled from 'styled-components'
import TextButton from 'LuxKit/Button/TextButton'
import Group from 'components/utils/Group'
import LayoutContainer from 'components/Common/LayoutContainer'
import CruiseSoldoutStillInterested from './CruiseSoldoutStillInterested'
import { useIntl } from 'react-intl'
import commonMessages from 'i18n/commonMessages'

const CruiseUnavailabilityWrapper = styled.div`
  padding: ${rem(40)} 0;
`

const CardContent = styled.div``

const CardActions = styled.div`
  display: flex;
  flex-direction: column;
  border-top: 1px solid ${props => props.theme.palette.neutral.surface.lightForeground};
  padding: ${rem(16)};
  height: 100%;
  align-items: center;
  text-align: center;

  ${mediaQueryUp.tablet} {
    text-align: start;
    margin-inline-start: auto;
    align-items: flex-start;
    border-top: none;
    border-inline-start: 1px solid ${props => props.theme.palette.neutral.surface.lightForeground};
    padding: 0;
    padding-inline-start: ${rem(24)};
  }
`

const CardBody = styled.div`
  display: flex;
  flex-direction: column;
  padding: ${rem(16)};
  gap: ${rem(16)};

  ${mediaQueryUp.tablet} {
    padding: ${rem(24)};
    gap: ${rem(24)};
    flex-direction: row;
    align-items: center;

    > ${CardContent} {
      flex-grow: 1;
    }

    > ${CardActions} {
      flex-shrink: 0;
    }
  }
`

const CardImageContainer = styled.div`
  > * {
    height: 100%;
  }
`

const UnavailabilityCard = styled.div`
  display: flex;
  flex-direction: column;
  border: 1px solid ${props => props.theme.palette.neutral.surface.lightForeground};
  background-color: ${props => props.theme.palette.neutral.surface.normalBackground};

  ${mediaQueryUp.tablet} {
    flex-direction: row;
  }

  > ${CardImageContainer} {
    flex-basis: ${rem(230)};
  }

  > ${CardBody} {
    flex-grow: 1;
  }
`

interface Props {
  offer: App.CruiseOffer
}

function CruiseUnavailabilityCard(props: Props) {
  const { offer } = props
  const offerImage = offer.images[0]
  const intl = useIntl()

  return <CruiseUnavailabilityWrapper>
    <LayoutContainer>
      <Group direction="vertical" gap={32}>
        <UnavailabilityCard>
          {!!offerImage?.id && <CardImageContainer>
            <ResponsiveImage aspectRatio="5:3" id={offerImage.id} mobileWidth="230px" fit="center" />
          </CardImageContainer>}
          <CardBody>
            <div>
              <Heading variant="heading5">
                {intl.formatMessage({
                  id: 'OLTr80Yof8uC-CzViBoW',
                  defaultMessage: 'Sorry, this offer has sold out!',
                  description: 'Heading shown when a cruise offer is sold out in the cruises section',
                })}
              </Heading>
              <BodyText variant="medium">{offer.name}</BodyText>
            </div>
            <CardActions>
              <CruiseSoldoutStillInterested />
            </CardActions>
          </CardBody>
        </UnavailabilityCard>

        <Group direction="vertical" horizontalAlign="center" gap={16}>
          <Heading variant="heading3" colour="normal-contrast">
            {intl.formatMessage({
              id: 'R3S_dPYfWaVuwlsXuM5f',
              defaultMessage: 'More offers available',
              description: 'Heading above browse all cruise offers link in the cruise section',
            })}
          </Heading>
          <TextButton kind="secondary" to="/cruises">
            {intl.formatMessage(commonMessages.browseAllCruiseOffers)}
          </TextButton>
        </Group>
      </Group>
    </LayoutContainer>
  </CruiseUnavailabilityWrapper>
}

export default CruiseUnavailabilityCard
